import React, { useState, useEffect, useCallback } from 'react';
import { EnrollmentApi } from '../../services/api/EnrollmentApi';
import { InviteApi } from '../../services/api/InviteApi';
import { CourseInviteApi } from '../../services/api/CourseInviteApi';
import './CourseManageTab.css';

/**
 * CourseInvitePanel — unified invitation UI combining shareable invite codes
 * (InviteApi) and direct email invitations (EnrollmentApi.inviteStudent).
 *
 * Reused by CourseManageTab (owner-facing shared-course manage view) and the
 * Course Admin Dashboard (/course/:id/admin).
 *
 * Props:
 *   - endeavorId: required. Course id.
 *   - onRosterChanged: optional callback fired after an email invite succeeds
 *     so parent rosters can refresh.
 */
export default function CourseInvitePanel({ endeavorId, onRosterChanged }) {
  const [reloadPending, setReloadPending] = useState(0);

  const handleInvited = useCallback(() => {
    setReloadPending((k) => k + 1);
    if (typeof onRosterChanged === 'function') onRosterChanged();
  }, [onRosterChanged]);

  return (
    <div className="course-invite-panel">
      <EmailInviteSubsection endeavorId={endeavorId} onRosterChanged={handleInvited} />
      <PendingEmailInvitesSubsection endeavorId={endeavorId} reloadKey={reloadPending} />
      <InviteCodesSubsection endeavorId={endeavorId} />
    </div>
  );
}

/* ── Pending Email Invitations Subsection ── */
function PendingEmailInvitesSubsection({ endeavorId, reloadKey }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await CourseInviteApi.getEndeavorInvites(endeavorId);
      if (resp.ok) {
        const data = await resp.json();
        setInvites(Array.isArray(data) ? data : []);
      } else {
        setError(`Failed to load pending invitations (${resp.status})`);
      }
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [endeavorId]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const handleRevoke = async (inviteId) => {
    try {
      const resp = await CourseInviteApi.revokeInvite(inviteId);
      if (resp.ok) setInvites((prev) => prev.filter((i) => i.id !== inviteId));
    } catch {
      /* leave in place so the user can retry */
    }
  };

  if (!loading && invites.length === 0) return null;

  return (
    <div className="course-manage-section">
      <h3>Pending Invitations</h3>
      <p className="course-manage-hint">Students who have been emailed but haven't accepted yet.</p>
      {loading ? (
        <p className="course-manage-hint">Loading…</p>
      ) : (
        <ul className="pending-invites-list">
          {invites.map((inv) => (
            <li key={inv.id} className="pending-invite-row">
              <span className="pending-invite-email">{inv.inviteeEmail}</span>
              <button className="btn btn-ghost" onClick={() => handleRevoke(inv.id)}>Revoke</button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="roster-error">{error}</p>}
    </div>
  );
}

/* ── Email Invite Subsection ── */
function EmailInviteSubsection({ endeavorId, onRosterChanged }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleInvite = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const resp = await EnrollmentApi.inviteStudent(endeavorId, trimmed);
      if (resp.ok) {
        setEmail('');
        setSuccess(`Invitation sent to ${trimmed}.`);
        if (typeof onRosterChanged === 'function') onRosterChanged();
      } else {
        const text = await resp.text().catch(() => '');
        setError(text || `Failed to invite (${resp.status})`);
      }
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="course-manage-section">
      <h3>Invite by Email</h3>
      <p className="course-manage-hint">Send a direct email invitation to a specific student.</p>
      <div className="roster-invite-row">
        <input
          type="email"
          placeholder="student@example.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="roster-email-input"
          onKeyDown={e => e.key === 'Enter' && handleInvite()}
          disabled={submitting}
        />
        <button
          className="btn btn-primary"
          onClick={handleInvite}
          disabled={submitting || !email.trim()}
        >
          {submitting ? 'Inviting…' : 'Send Invite'}
        </button>
      </div>
      {error && <p className="roster-error" role="alert">{error}</p>}
      {success && <p className="roster-success">{success}</p>}
    </div>
  );
}

/* ── Shareable Invite Codes Subsection ── */
function InviteCodesSubsection({ endeavorId }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [maxUses, setMaxUses] = useState('');
  const [expiresIn, setExpiresIn] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState(null);

  const loadInvites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await InviteApi.getInvites(endeavorId);
      if (resp.ok) {
        const data = await resp.json();
        setInvites(Array.isArray(data) ? data : []);
      } else {
        setError(`Failed to load invites (${resp.status})`);
      }
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [endeavorId]);

  useEffect(() => { loadInvites(); }, [loadInvites]);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const options = {};
      if (maxUses) options.maxUses = parseInt(maxUses, 10);
      if (expiresIn) {
        const hours = parseInt(expiresIn, 10);
        options.expiresAt = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
      }
      const resp = await InviteApi.createInvite(endeavorId, options);
      if (resp.ok) {
        await loadInvites();
        setMaxUses('');
        setExpiresIn('');
      } else {
        setError(`Failed to create code (${resp.status})`);
      }
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (inviteId) => {
    try {
      const resp = await InviteApi.deactivateInvite(inviteId);
      if (resp.ok) await loadInvites();
      else setError(`Failed to deactivate (${resp.status})`);
    } catch (err) {
      setError(err?.message || 'Network error');
    }
  };

  const handleCopy = (code, id) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  return (
    <div className="course-manage-section">
      <h3>Invite Codes</h3>
      <p className="course-manage-hint">Generate a shareable code students can redeem to join.</p>

      <div className="invite-create-row">
        <input
          type="number"
          placeholder="Max uses (optional)"
          value={maxUses}
          onChange={e => setMaxUses(e.target.value)}
          className="invite-input"
          min="1"
        />
        <select value={expiresIn} onChange={e => setExpiresIn(e.target.value)} className="invite-select">
          <option value="">No expiry</option>
          <option value="24">24 hours</option>
          <option value="72">3 days</option>
          <option value="168">7 days</option>
          <option value="720">30 days</option>
        </select>
        <button className="btn btn-primary invite-create-btn" onClick={handleCreate} disabled={creating}>
          {creating ? 'Creating…' : 'Generate Code'}
        </button>
      </div>

      {error && <p className="roster-error" role="alert">{error}</p>}

      {loading ? (
        <p className="course-manage-loading">Loading invites…</p>
      ) : invites.length === 0 ? (
        <p className="course-manage-empty">No active invite codes. Generate one above.</p>
      ) : (
        <div className="invite-list">
          {invites.map(invite => {
            const id = invite.inviteId || invite.id;
            const code = invite.inviteCode || invite.code;
            return (
              <div key={id} className="invite-card">
                <div className="invite-card-code">
                  <code>{code}</code>
                  <button
                    className="invite-copy-btn"
                    onClick={() => handleCopy(code, id)}
                    title="Copy code"
                  >
                    {copiedId === id ? '✓' : '📋'}
                  </button>
                </div>
                <div className="invite-card-meta">
                  <span>Uses: {invite.usesCount || 0}{invite.maxUses ? ` / ${invite.maxUses}` : ''}</span>
                  {invite.expiresAt && <span>Expires: {new Date(invite.expiresAt).toLocaleDateString()}</span>}
                </div>
                <button className="btn btn-sm btn-danger" onClick={() => handleDeactivate(id)}>
                  Deactivate
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
